import React, { useState, useEffect } from 'react';

interface UserAvatarProps {
  src?: string;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  objectPosition?: string;
  style?: React.CSSProperties;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  alt = 'User',
  size = 'md',
  className = '',
  objectPosition = 'center',
  style,
}) => {
  const [imgError, setImgError] = useState(false);
  const [displaySrc, setDisplaySrc] = useState<string | undefined>(src);

  useEffect(() => {
    setDisplaySrc(src);
    setImgError(false);
  }, [src]);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-base', // slightly larger than w-12 for 'lg' based on typical usage
    xl: 'w-20 h-20 text-xl',
  };

  const handleError = () => {
    setImgError(true);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  if (!displaySrc || imgError) {
    return (
      <div
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-medium overflow-hidden ${className}`}
        style={{ backgroundColor: 'var(--color-brand-600)', ...style }}
      >
        {getInitials(alt)}
      </div>
    );
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
      onError={handleError}
      loading="lazy"
      decoding="async"
      style={{ objectPosition, ...style }}
    />
  );
};

export default UserAvatar;
